import React, { FC } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { isPhone } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import Histogram from '@v2/elements/Histogram';
import ColorWrapper from '@styles/colors';
import {
  LINE_HORIZONTAL_PADDING,
  SCREEN_BORDER_RADIUS,
  SCREEN_MARGIN_BOTTOM_PHONE,
  SCREEN_MARGIN_BOTTOM_WEB,
} from '../../../../../../v2/pages/common/constants/base.constants';
import BatchDetailsBlockContent from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import ProductivityBlockWithTimelines from '@components/common/ProductivityBlockWithTimelines';
import { useManageBatch } from './hooks/useManageBatch';
import type { RunningBatchProps } from '@v2/pages/machines/common/components/RunningBatch/RunningBatch';
import type { ProductionBatchStatistics } from '@interfaces/search';
import type { WithColor } from '@styles/colors';
import type { Machine } from '@interfaces/machines';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface BatchProps extends WithColor, Omit<RunningBatchProps, 'batch'> {
  detailsWidth: number;
  maxDetailsWidth: number;
  batch: ProductionBatchStatistics | null;
  selectedItem?: Machine;
  bg?: string;
}

const Batch: FC<BatchProps> = (props) => {
  const { C, batch, batchLoadedTime, batchLoading, loadBatch, maxDetailsWidth, withImages, bg } = props;

  const {
    showPerPcs,
    toggleShowPerPcs,
    isQuantityValid,
    showChartPercentiles,
    roundTopConfig,
    shadowStyles,
    chartWidth,
    quantityReady,
    actualSetupTimeInSeconds,
    paidSetupTimeInSeconds,
    idealSetupTimeInSeconds,
    p25ActiveTimeInSeconds,
    p50ActiveTimeInSeconds,
    p75ActiveTimeInSeconds,
    p25ChangeTimeInSeconds,
    p50ChangeTimeInSeconds,
    p75ChangeTimeInSeconds,
    filteredProductiveTimeData,
    filteredSetupTimeData,
    filteredActiveTimeData,
    filteredChangeTimeData,
  } = useManageBatch(props);

  return (
    <View style={{ marginBottom: scale(isPhone ? SCREEN_MARGIN_BOTTOM_PHONE : SCREEN_MARGIN_BOTTOM_WEB) }}>
      <BatchDetailsBlockContent {...{ batch, batchLoading, maxDetailsWidth, withImages, bg }} />

      {!batch?.isInProgress && (
        <>
          <ProductivityBlockWithTimelines
            {...{
              C,
              data: filteredProductiveTimeData,
              label: i18n.t('TIME DISTRIBUTION AFTER {{quantityReady}} PIECES', { quantityReady: batch?.quantityReady ?? 0 }),
              labelAlreadyTranslated: true,
              onlyLabel: true,
            }}
          />

          <View style={{ flexDirection: 'row', alignItems: 'center', paddingLeft: scale(LINE_HORIZONTAL_PADDING), marginTop: scale(8) }}>
            <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginRight: scale(8) }]}>
              {i18n.t('Last updated')} {batchLoadedTime}.
            </Text>
            <TouchableElement testID={TEST_IDS.runningBatch.reloadButton} onPress={loadBatch}>
              {({ boxStyle, pressHoverColor }) => {
                return (
                  <View
                    style={[
                      boxStyle,
                      {
                        height: scale(17),
                        width: scale(17),
                        backgroundColor: C(pressHoverColor('Button/Light/Secondary')),
                        borderRadius: scale(17),
                        justifyContent: 'center',
                        alignItems: 'center',
                      },
                    ]}
                  >
                    <Svg icon="reload" width={scale(9)} height={scale(10)} color="Button/Light/Tertiary Text" />
                  </View>
                );
              }}
            </TouchableElement>
          </View>

          <View testID={TEST_IDS.runningBatch.setupHistogram} style={{ marginTop: scale(26) }}>
            <Text
              style={[font.Body, { color: C('Label/Light/Tertiary'), paddingHorizontal: scale(LINE_HORIZONTAL_PADDING), marginBottom: scale(5) }]}
            >
              {i18n.t('SETUP TIME')}
            </Text>
            <View
              style={[shadowStyles, { width: '100%', backgroundColor: C(bg), borderRadius: scale(SCREEN_BORDER_RADIUS), paddingBottom: scale(10) }]}
            >
              <View style={{ width: '100%', alignItems: 'center', marginVertical: scale(15) }}>
                <Histogram
                  {...{
                    data: filteredSetupTimeData,
                    mainColor: 'Status/Light/Setup',
                    P25: Math.min(actualSetupTimeInSeconds, paidSetupTimeInSeconds, idealSetupTimeInSeconds),
                    P75: Math.max(actualSetupTimeInSeconds, paidSetupTimeInSeconds, idealSetupTimeInSeconds),
                    elementWidth: chartWidth,
                    loaded: !batchLoading,
                    showPercentiles: false,
                    hiddenNullPercentiles: true,
                  }}
                />
              </View>
            </View>
          </View>

          <View testID={TEST_IDS.runningBatch.activeChangeHistograms} style={{ marginTop: scale(26) }}>
            <View
              style={{ flexDirection: 'row', justifyContent: 'space-between', paddingLeft: scale(LINE_HORIZONTAL_PADDING), marginBottom: scale(5) }}
            >
              {!isPhone && (
                <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>
                  {i18n.t(showPerPcs ? 'ACTIVE & CHANGE AVERAGE PER PCS' : `ACTIVE & CHANGE FOR {{quantityReady}} PCS`, {
                    quantityReady: quantityReady ?? 0,
                  })}
                </Text>
              )}

              {isPhone && (
                <View>
                  <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>{i18n.t('ACTIVE & CHANGE')}</Text>
                  <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>
                    {i18n.t(showPerPcs ? 'AVERAGE PER PCS' : `FOR {{quantityReady}} PCS`, {
                      quantityReady: quantityReady ?? 0,
                    })}
                  </Text>
                </View>
              )}

              {isQuantityValid ? (
                <TouchableElement testID={TEST_IDS.runningBatch.toggleButton} onPress={toggleShowPerPcs}>
                  {({ boxStyle, pressHoverColor }) => {
                    return (
                      <Text
                        style={[
                          boxStyle,
                          font.Body,
                          {
                            marginTop: 'auto',
                            backgroundColor: C(pressHoverColor('Button/Light/Only Text')),
                            color: C(pressHoverColor('Link/Light/Primary')),
                            textTransform: 'uppercase',
                            borderRadius: scale(18),
                            paddingHorizontal: scale(10),
                            userSelect: 'none',
                          },
                        ]}
                      >
                        {i18n.t(showPerPcs ? 'Show total' : 'Show per pcs')}
                      </Text>
                    );
                  }}
                </TouchableElement>
              ) : null}
            </View>
            <View style={[shadowStyles, roundTopConfig]}>
              <View style={{ width: '100%', backgroundColor: C(bg), borderRadius: scale(SCREEN_BORDER_RADIUS) }}>
                <View style={{ width: '100%', alignItems: 'center', marginVertical: scale(15) }}>
                  <Histogram
                    {...{
                      data: filteredActiveTimeData,
                      mainColor: 'Status/Light/Active',
                      P25: p25ActiveTimeInSeconds,
                      P50: p50ActiveTimeInSeconds,
                      P75: p75ActiveTimeInSeconds,
                      elementWidth: chartWidth,
                      showPercentiles: showChartPercentiles,
                      loaded: !batchLoading,
                    }}
                  />
                </View>
                <View style={{ width: '100%', alignItems: 'center', marginVertical: scale(15) }}>
                  <Histogram
                    {...{
                      data: filteredChangeTimeData,
                      mainColor: 'Status/Light/Change',
                      P25: p25ChangeTimeInSeconds,
                      P50: p50ChangeTimeInSeconds,
                      P75: p75ChangeTimeInSeconds,
                      elementWidth: chartWidth,
                      showPercentiles: showChartPercentiles,
                      loaded: !batchLoading,
                    }}
                  />
                </View>
              </View>
            </View>
          </View>
        </>
      )}
    </View>
  );
};

export default ColorWrapper(Batch);
